import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import classnames from "classnames";
import useIsMounted from "../../../hooks/use-is-mounted";
import {createUUId, getFirstScrollingAncestor} from "../../../../core/services/utils/utils";
import ObserversService from "../../../../core/services/observers";
import {uniquifySvgIds} from "./uniquify-svg-ids";

// noinspection JSSuspiciousNameCombination
const LazySvg = ({
                     src,
                     noSvg = undefined,
                     height: heightProps,
                     width: widthProps = heightProps,
                     onError = undefined,
                     onLoad = undefined,
                     ...props
                 }) => {
    const [svg, setSvg] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const ancestorFinderRef = useRef();
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const elementRef = useRef();
    /**@type {React.MutableRefObject<string>}*/
    const instancePrefixRef = useRef(`${createUUId(true)}_`);

    const height = useMemo(() => typeof heightProps === 'function' ? heightProps() : heightProps, [heightProps])
    const width = useMemo(() => typeof widthProps === 'function' ? widthProps() : widthProps, [widthProps])
    /**@type {React.MutableRefObject<(() => void) | undefined>}*/
    const onLoadRef = useRef(onLoad);
    /**@type {React.MutableRefObject<(() => void) | undefined>}*/
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onLoadRef.current = onLoad;
        onErrorRef.current = onError;
    }, [onLoad, onError]);

    /**
     * Loads the svg from the given src and sets the state.
     * @return {Promise<void>}
     */
    const fetchSvg = useCallback(async () => {
        setLoaded(false);
        setError(false);
        try {
            const response = await fetch(src);
            if (response.status !== 200) {
                // noinspection ExceptionCaughtLocallyJS
                throw Error(`Failed to load svg with status code ${response.status}`);
            }
            const rawSvg = await response.text();
            if (!isMounted())
                return
            setSvg(uniquifySvgIds(rawSvg, instancePrefixRef.current))
            onLoadRef.current?.();
        } catch (e) {
            onErrorRef.current?.();
            if (isMounted()) setError(true);
        } finally {
            if (isMounted()) setLoaded(true);
        }
    }, [src, isMounted]);

    /**
     * With each change in src:
     * - attaches an intersection observer that would load the svg only when visible.
     */
    useEffect(() => {
        /**
         * Observes the intersection of the current svg with the viewport and loads the svg as soon as the element is
         * inside the viewport.
         *
         * @param {IntersectionObserverEntry[]} entries
         * @param {IntersectionObserver} observer
         */
        const observeIntersections = (entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    fetchSvg().then();
                    observer.unobserve(entry.target);
                }
            });
        }

        /**@type {Partial<IntersectionObserverInit>}*/
        const config = {
            root: getFirstScrollingAncestor(ancestorFinderRef.current),
            rootMargin: '20%',
            threshold: 0,
        }
        const observer = ObserversService.newIntersectionObserver(observeIntersections, config);
        ObserversService.observeIntersectionObserver(observer, elementRef.current);
        return () => ObserversService.disconnectIntersectionObserver(observer);
    }, [src, ancestorFinderRef, noSvg, fetchSvg]);

    return (
        <>
            <div ref={ancestorFinderRef} style={{display: "none"}}/>
            <div ref={elementRef} style={{height: height, width: width}} {...props}>
                {
                    error &&
                    (
                        React.isValidElement(noSvg)
                            ? noSvg
                            : <React.Fragment/>
                    )
                }
                {
                    !error &&
                    <span className={'lazy-svg-background'} style={{height: height, width: width}}>
                    {
                        <div
                            className={classnames('lazy-svg', {'lazy-svg--loaded': loaded},)}
                            style={{height: height, width: width}}
                            dangerouslySetInnerHTML={{__html: svg}}
                        />
                    }
                </span>
                }
            </div>
        </>
    );
}

export default LazySvg;
